import { useShallow } from 'zustand/react/shallow';

import { cn } from '@nesso/ui/lib/utils';

import {
    useDisplayMediaStore,
    useMicStore,
    useRecordingControls,
    useRecordingStore,
    useSilenceDetection,
} from '@/entities/audio-recording';
import { useNavigationBlocker } from '@/shared/lib/use-navigation-blocker';

import { RecordingCaptureGenerateNoteModal } from './recording-capture/recording-capture-generate-note-modal';
import { RecordingCaptureIndicator } from './recording-capture/recording-capture-indicator';
import { RecordingCaptureLabeledAudioMeter } from './recording-capture/recording-capture-labeled-audio-meter';
import { RecordingCapturePause } from './recording-capture/recording-capture-pause';
import { RecordingCaptureSettings } from './recording-capture/recording-capture-settings';
import { RecordingCaptureStop } from './recording-capture/recording-capture-stop';
import { RecordingCaptureTimer } from './recording-capture/recording-capture-timer';

export function RecordingCapture() {
    const status = useRecordingStore((state) => state.status);

    const { displayMediaStream } = useDisplayMediaStore(
        useShallow((state) => ({
            displayMediaStream: state.currentStream,
        })),
    );

    const { abort } = useRecordingControls();

    const { detectSilence } = useSilenceDetection();

    useNavigationBlocker({
        title: 'Leave recording?',
        description:
            'Recording in progress. Are you sure you want to leave? Your recording will be lost.',
        onConfirmLeave: abort,
    });

    const micStream = useMicStore((x) => x.currentStream);

    return (
        <div className="sm:w-82.5 flex flex-col items-center justify-center">
            <div className="flex items-center justify-center gap-2 mb-2 sm:mb-9">
                {status === 'recording' && <RecordingCaptureIndicator animated={true} />}
                <p className={status === 'recording' ? 'text-secondary' : 'text-muted-foreground'}>
                    {status === 'recording' ? 'Recording' : 'Recording paused'}
                </p>
            </div>
            <div className="mb-7 sm:mb-9">
                <RecordingCaptureTimer />
            </div>
            <div
                className={cn(
                    'flex flex-col items-center mb-8 sm:mb-16',
                    displayMediaStream ? 'gap-10 mt-10' : 'gap-4',
                )}
            >
                <RecordingCaptureLabeledAudioMeter
                    label="your microphone"
                    showLabel={!!displayMediaStream}
                    onAudioLevelChange={detectSilence}
                    audioStream={micStream}
                />

                {displayMediaStream && (
                    <RecordingCaptureLabeledAudioMeter
                        label={`client's microphone`}
                        audioStream={displayMediaStream}
                    />
                )}
            </div>
            <div className="flex items-center justify-center gap-4">
                <RecordingCaptureSettings />
                <RecordingCapturePause />
                <RecordingCaptureStop />
            </div>
            <RecordingCaptureGenerateNoteModal />
        </div>
    );
}
